import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  Error as DangerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

const Senders = () => {
  const [stats, setStats] = useState(null);
  const [knownSenders, setKnownSenders] = useState([]);
  const [suspiciousSenders, setSuspiciousSenders] = useState([]);
  const [recentVerifications, setRecentVerifications] = useState([]);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Dialog states
  const [knownDialog, setKnownDialog] = useState(false);
  const [suspiciousDialog, setSuspiciousDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  
  // Form states
  const [knownForm, setKnownForm] = useState({
    senderValue: '',
    senderType: 'short_code',
    brandName: '',
    category: 'other',
    description: '',
    websiteUrl: ''
  });
  
  const [suspiciousForm, setSuspiciousForm] = useState({
    senderValue: '',
    senderType: 'phone',
    riskLevel: 'medium',
    reason: '',
    impersonates: ''
  });
  
  const [testSender, setTestSender] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await adminAPI.getSenderStats({ days: 30 });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadKnownSenders = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await adminAPI.getKnownSenders(params);
      setKnownSenders(res.data.senders || []);
    } catch (err) {
      console.error('Failed to load known senders:', err);
    }
  }, [typeFilter, categoryFilter]);

  const loadSuspiciousSenders = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      const res = await adminAPI.getSuspiciousSenders(params);
      setSuspiciousSenders(res.data.senders || []);
    } catch (err) {
      console.error('Failed to load suspicious senders:', err);
    }
  }, [typeFilter]);

  const loadRecentVerifications = useCallback(async () => {
    try {
      const res = await adminAPI.getRecentVerifications({ limit: 50, onlyFlagged: 'true' });
      setRecentVerifications(res.data.verifications || []);
    } catch (err) {
      console.error('Failed to load verifications:', err);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await adminAPI.getSenderCategories();
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    await Promise.all([
      loadStats(),
      loadKnownSenders(),
      loadSuspiciousSenders(),
      loadRecentVerifications(),
      loadCategories(),
    ]);
    setLoading(false);
  }, [loadStats, loadKnownSenders, loadSuspiciousSenders, loadRecentVerifications, loadCategories]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadKnownSenders();
    loadSuspiciousSenders();
  }, [typeFilter, categoryFilter, loadKnownSenders, loadSuspiciousSenders]);

  const handleAddKnown = async () => {
    try {
      await adminAPI.createKnownSender(knownForm);
      setKnownDialog(false);
      setKnownForm({ senderValue: '', senderType: 'short_code', brandName: '', category: 'other', description: '', websiteUrl: '' });
      loadKnownSenders();
      loadStats();
    } catch (err) {
      console.error('Failed to add known sender:', err);
      alert('Failed to add known sender');
    }
  };

  const handleDeleteKnown = async (id) => {
    if (!window.confirm('Remove this known sender?')) return;
    try {
      await adminAPI.deleteKnownSender(id);
      loadKnownSenders();
      loadStats();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleAddSuspicious = async () => {
    try {
      await adminAPI.createSuspiciousSender(suspiciousForm);
      setSuspiciousDialog(false);
      setSuspiciousForm({ senderValue: '', senderType: 'phone', riskLevel: 'medium', reason: '', impersonates: '' });
      loadSuspiciousSenders();
      loadStats();
    } catch (err) {
      console.error('Failed to add suspicious sender:', err);
      alert('Failed to add suspicious sender');
    }
  };

  const handleDeleteSuspicious = async (id) => {
    if (!window.confirm('Remove this suspicious sender?')) return;
    try {
      await adminAPI.deleteSuspiciousSender(id);
      loadSuspiciousSenders();
      loadStats();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleTestVerify = async () => {
    if (!testSender.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAPI.testVerifySender({ sender: testSender.trim() });
      setTestResult(res.data.result);
    } catch (err) {
      console.error('Failed to verify:', err);
      setTestResult({ error: 'Verification failed' });
    }
    setTesting(false);
  };

  const getRiskChip = (level, score) => {
    const color = level === 'high' ? 'error' : level === 'moderate' ? 'warning' : 'success';
    const icon = level === 'high' ? <DangerIcon /> : level === 'moderate' ? <WarningIcon /> : <VerifiedIcon />;
    return <Chip icon={icon} label={`${level} (${score})`} color={color} size="small" />;
  };

  const getTypeIcon = (type) => {
    if (type === 'email' || type === 'domain') return <EmailIcon fontSize="small" />;
    return <PhoneIcon fontSize="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Sender Verification</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button startIcon={<SearchIcon />} variant="outlined" size="small" onClick={() => setTestDialog(true)}>
            Test Verification
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={loadAll} variant="outlined" size="small">
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Verifications (30d)</Typography>
                    <Typography variant="h4">{stats.verifications.total}</Typography>
                  </Box>
                  <ShieldIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
                <Box display="flex" gap={1} mt={1}>
                  <Chip label={`${stats.verifications.highRisk} high`} size="small" color="error" variant="outlined" />
                  <Chip label={`${stats.verifications.moderateRisk} mod`} size="small" color="warning" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Known Senders</Typography>
                    <Typography variant="h4">{stats.database.knownSenders}</Typography>
                  </Box>
                  <VerifiedIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {stats.database.shortCodes} short codes, {stats.database.domains} domains
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Suspicious Senders</Typography>
                    <Typography variant="h4" color="error">{stats.database.suspiciousSenders}</Typography>
                  </Box>
                  <BlockIcon color="error" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Typosquat Detected</Typography>
                    <Typography variant="h4" color="warning.main">{stats.matches.typosquatDetected}</Typography>
                  </Box>
                  <WarningIcon color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Known Senders" icon={<VerifiedIcon />} iconPosition="start" />
          <Tab label="Suspicious Senders" icon={<BlockIcon />} iconPosition="start" />
          <Tab label="Recent Flags" icon={<WarningIcon />} iconPosition="start" />
        </Tabs>

        {/* Known Senders Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                  <InputLabel>Type</InputLabel>
                  <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {categories?.senderTypes?.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                  <InputLabel>Category</InputLabel>
                  <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {categories?.categories?.map(c => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setKnownDialog(true)}>
                Add Known Sender
              </Button>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Brand</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {knownSenders.map((sender) => (
                    <TableRow key={sender.id}>
                      <TableCell>{getTypeIcon(sender.type)} {sender.type}</TableCell>
                      <TableCell><code>{sender.value}</code></TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{sender.brandName}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Chip label={sender.category} size="small" /></TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{sender.source}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteKnown(sender.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {knownSenders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No known senders found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Suspicious Senders Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {categories?.senderTypes?.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button startIcon={<AddIcon />} variant="contained" color="error" size="small" onClick={() => setSuspiciousDialog(true)}>
                Add Suspicious Sender
              </Button>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Risk Level</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Impersonates</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Reports</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suspiciousSenders.map((sender) => (
                    <TableRow key={sender.id}>
                      <TableCell>{getTypeIcon(sender.type)} {sender.type}</TableCell>
                      <TableCell><code>{sender.value}</code></TableCell>
                      <TableCell>
                        <Chip
                          label={sender.riskLevel}
                          size="small"
                          color={sender.riskLevel === 'high' ? 'error' : sender.riskLevel === 'medium' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{sender.reason || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{sender.impersonates || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{sender.reportCount}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteSuspicious(sender.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {suspiciousSenders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">No suspicious senders found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Recent Flags Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Recent Flagged Verifications</Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Sender</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Issues</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentVerifications.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell><code>{v.senderValue}</code></TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{v.senderType}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{v.channel || '-'}</TableCell>
                      <TableCell>{getRiskChip(v.riskScore >= 50 ? 'high' : v.riskScore >= 25 ? 'moderate' : 'low', v.riskScore)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {v.typosquatDetected && <Chip label={`Typosquat: ${v.typosquatTarget}`} size="small" color="warning" sx={{ mr: 0.5 }} />}
                        {v.isKnownSuspicious && <Chip label="Known suspicious" size="small" color="error" />}
                        {!v.formatValid && <Chip label="Invalid format" size="small" />}
                      </TableCell>
                      <TableCell>{format(new Date(v.verifiedAt), 'MMM dd HH:mm')}</TableCell>
                    </TableRow>
                  ))}
                  {recentVerifications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No flagged verifications</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Add Known Sender Dialog */}
      <Dialog open={knownDialog} onClose={() => setKnownDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Known Sender</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Sender Type</InputLabel>
              <Select 
                value={knownForm.senderType} 
                label="Sender Type" 
                onChange={(e) => setKnownForm({ ...knownForm, senderType: e.target.value })}
              >
                {categories?.senderTypes?.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              label="Sender Value" 
              fullWidth 
              value={knownForm.senderValue} 
              onChange={(e) => setKnownForm({ ...knownForm, senderValue: e.target.value })}
              placeholder={knownForm.senderType === 'domain' ? 'amazon.com' : knownForm.senderType === 'short_code' ? '32665' : '+15551234567'}
            />
            <TextField 
              label="Brand Name" 
              fullWidth 
              value={knownForm.brandName} 
              onChange={(e) => setKnownForm({ ...knownForm, brandName: e.target.value })}
              placeholder="Amazon"
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select 
                value={knownForm.category} 
                label="Category" 
                onChange={(e) => setKnownForm({ ...knownForm, category: e.target.value })}
              >
                {categories?.categories?.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              label="Description" 
              fullWidth 
              multiline 
              rows={2}
              value={knownForm.description} 
              onChange={(e) => setKnownForm({ ...knownForm, description: e.target.value })}
            />
            <TextField 
              label="Website URL" 
              fullWidth 
              value={knownForm.websiteUrl} 
              onChange={(e) => setKnownForm({ ...knownForm, websiteUrl: e.target.value })}
              placeholder="https://www.amazon.com"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKnownDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddKnown}
            disabled={!knownForm.senderValue || !knownForm.brandName}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Suspicious Sender Dialog */}
      <Dialog open={suspiciousDialog} onClose={() => setSuspiciousDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Suspicious Sender</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Sender Type</InputLabel>
              <Select 
                value={suspiciousForm.senderType} 
                label="Sender Type" 
                onChange={(e) => setSuspiciousForm({ ...suspiciousForm, senderType: e.target.value })}
              >
                {categories?.senderTypes?.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              label="Sender Value" 
              fullWidth 
              value={suspiciousForm.senderValue} 
              onChange={(e) => setSuspiciousForm({ ...suspiciousForm, senderValue: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Risk Level</InputLabel>
              <Select 
                value={suspiciousForm.riskLevel} 
                label="Risk Level" 
                onChange={(e) => setSuspiciousForm({ ...suspiciousForm, riskLevel: e.target.value })}
              >
                {categories?.riskLevels?.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              label="Reason" 
              fullWidth 
              multiline 
              rows={2}
              value={suspiciousForm.reason} 
              onChange={(e) => setSuspiciousForm({ ...suspiciousForm, reason: e.target.value })}
              placeholder="Why is this sender suspicious?"
            />
            <TextField 
              label="Impersonates" 
              fullWidth 
              value={suspiciousForm.impersonates} 
              onChange={(e) => setSuspiciousForm({ ...suspiciousForm, impersonates: e.target.value })}
              placeholder="e.g., Amazon, IRS"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspiciousDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleAddSuspicious}
            disabled={!suspiciousForm.senderValue}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Verification Dialog */}
      <Dialog open={testDialog} onClose={() => { setTestDialog(false); setTestResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Test Sender Verification</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField 
              label="Phone Number or Email" 
              fullWidth 
              value={testSender} 
              onChange={(e) => setTestSender(e.target.value)}
              placeholder="+15551234567 or test@example.com"
              onKeyDown={(e) => e.key === 'Enter' && handleTestVerify()}
            />
            <Button 
              variant="contained" 
              onClick={handleTestVerify} 
              disabled={testing || !testSender.trim()}
              sx={{ mt: 2 }}
              fullWidth
            >
              {testing ? <CircularProgress size={24} /> : 'Verify'}
            </Button>

            {testResult && (
              <Box sx={{ mt: 3 }}>
                {testResult.error ? (
                  <Alert severity="error">{testResult.error}</Alert>
                ) : (
                  <Box>
                    <Alert severity={testResult.riskLevel === 'high' ? 'error' : testResult.riskLevel === 'moderate' ? 'warning' : 'success'}>
                      <strong>Risk Level:</strong> {testResult.riskLevel} (Score: {testResult.riskScore})
                    </Alert>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Details:</Typography>
                      <Typography variant="body2">Type: {testResult.senderType}</Typography>
                      <Typography variant="body2">Format Valid: {testResult.formatValid ? 'Yes' : 'No'}</Typography>
                      {testResult.formatIssues?.length > 0 && (
                        <Typography variant="body2" color="error">Issues: {testResult.formatIssues.join(', ')}</Typography>
                      )}
                      {testResult.isKnownLegitimate && (
                        <Chip label={`Verified: ${testResult.knownSenderBrand}`} color="success" size="small" sx={{ mt: 1 }} />
                      )}
                      {testResult.isKnownSuspicious && (
                        <Chip label="Known Suspicious" color="error" size="small" sx={{ mt: 1 }} />
                      )}
                      {testResult.typosquatDetected && (
                        <Chip label={`Typosquat of ${testResult.typosquatTarget} (${testResult.typosquatSimilarity}%)`} color="warning" size="small" sx={{ mt: 1 }} />
                      )}
                      {testResult.domainExists !== null && (
                        <Typography variant="body2">Domain Exists: {testResult.domainExists ? 'Yes' : 'No'}</Typography>
                      )}
                      {testResult.domainHasMx !== null && (
                        <Typography variant="body2">Has MX Records: {testResult.domainHasMx ? 'Yes' : 'No'}</Typography>
                      )}
                      {testResult.warnings?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {testResult.warnings.map((w, i) => (
                            <Typography key={i} variant="body2" color="warning.main">{w}</Typography>
                          ))}
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>Summary:</strong> {testResult.summary}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTestDialog(false); setTestResult(null); setTestSender(''); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Senders;
